import React, { useEffect, useState } from "react";
import { Avatar, Col, Modal, Row } from "antd";
import styled from "styled-components";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { FlexCol, FlexRow } from "../../../components/Container/Container";
import image from "../../../resource/image";
import Text from "../../../components/Text/Text";
import color from "../../../resource/color";
import { MAP_QUOTA_TYPE } from "../../../definitions/QuotaType";
import TablePagination from "../../../components/Table/TablePagination";

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    padding: 0;
  }
  .ant-modal-body {
    padding: 0;
  }
`;

const ScrollContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
`;

const datePattern = "DD/MM/YYYY";

interface Props {
  open: boolean;
  onCancel: () => void;
  datas: any[];
  cusName: string;
}

function ModalQuotaDup({ open, onCancel, datas, cusName }: Props) {
  const groupedData = datas.map((quota) => ({
    quotaInfo: {
      quotaId: quota.quotaId,
      quotaName: quota.quotaName,
      quotaType: quota.quotaType,
      startDate: quota.startDate,
      endDate: quota.endDate,
    },
    products: quota.productShop.map((product) => ({
      ...product,
      quotaId: quota.quotaId,
    })),
  }));

  const columns: any = [
    {
      title: "ชื่อสินค้า",
      key: "productName",
      render: (_: any, record: any, idx: number) => (
        <FlexRow
          key={record.productId || idx}
          align='center'
          style={{
            marginBottom: 12,
          }}
        >
          <Avatar
            src={
              !record.productImage || record.productImage === "No"
                ? image.product_no_image
                : record.productImage || record.productFreebiesImage
            }
            size={50}
            shape='square'
            onError={() => false}
            style={{ marginRight: 16 }}
          />
          <FlexCol>
            <Text level={5} fontWeight={600}>
              {record.productName}
            </Text>
            {record.commonName && (
              <Text level={6} color='background4'>
                {record.commonName}
              </Text>
            )}
            <Text level={6} color='background4'>
              Product Group: {record.productGroup ?? "-"}
            </Text>
            {record.productStrategy && (
              <Text level={6} color='background4'>
                Strategy Group: {record.productStrategy}
              </Text>
            )}
          </FlexCol>
        </FlexRow>
      ),
    },
    {
      title: "ข้อมูลเพิ่มเติม",
      key: "pack",
      render: (_: any, record: any) => (
        <FlexCol>
          {record.productCodeNav && (
            <Text level={6} color='background4'>
              Code Nav: {record.productCodeNav}
            </Text>
          )}
          {record.packSize && (
            <Text level={6} color='background4'>
              Pack Size: {record.packSize}
            </Text>
          )}
        </FlexCol>
      ),
    },
  ];

  return (
    <ModalStyled centered open={open} onCancel={onCancel} closeIcon width={900} footer={false}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "start", padding: 18 }}>
        <Text align='left' fontWeight={700} fontSize={24}>
          สินค้าที่เข้าร่วมกับโควตาอื่น
        </Text>
        <Text align='left' fontSize={16}>
          {cusName}
        </Text>
        <Row
          gutter={20}
          style={{
            backgroundColor: "#FFF5F5",
            padding: 10,
            borderRadius: 4,
            width: "100%",
            margin: 0,
          }}
        >
          <Col>
            <InfoCircleOutlined style={{ color: color.warning, fontSize: 20 }} />
          </Col>
          <Col span={22}>
            <Text align='left' fontWeight={400} fontSize={14} fontFamily='Sarabun'>
              สินค้าที่เข้าร่วมโควตาอื่นที่เปิดใช้งานจะไม่ได้รับโควตาในรอบนี้
              เนื่องจากมีการทับซ้อนกัน ส่วนสินค้าอื่นที่ไม่ได้เข้าร่วมโควตาใด จะได้รับโควตาตามปกติ
            </Text>
          </Col>
        </Row>
      </div>

      <ScrollContainer>
        {groupedData.map(({ quotaInfo, products }) => (
          <div key={quotaInfo.quotaId} style={{ marginBottom: 40 }}>
            <Row
              justify='space-between'
              style={{
                backgroundColor: "#EFF2F9",
                display: "flex",
                alignItems: "center",
                position: "relative",
                padding: 12,
                minHeight: 56,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  backgroundColor: color.primary,
                }}
              />

              <Col span={8} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text fontWeight={700} fontSize={14} color='background4'>
                  ชื่อโควตา:&nbsp;
                  <Text fontWeight={700} fontSize={14} color='black'>
                    {quotaInfo.quotaName || "-"}
                  </Text>
                </Text>
              </Col>

              <Col span={8}>
                <Text fontWeight={700} fontSize={14} color='background4'>
                  ประเภทโควตา:&nbsp;
                  <Text fontWeight={700} fontSize={14} color='black'>
                    {MAP_QUOTA_TYPE[quotaInfo.quotaType] || "-"}
                  </Text>
                </Text>
              </Col>

              <Col span={8}>
                <Text fontWeight={700} fontSize={14} color='background4'>
                  ช่วงเวลา:&nbsp;
                  <Text fontWeight={700} fontSize={14} color='black'>
                    {quotaInfo.startDate ? dayjs(quotaInfo.startDate).format(datePattern) : "-"} -{" "}
                    {quotaInfo.endDate ? dayjs(quotaInfo.endDate).format(datePattern) : "-"}
                  </Text>
                </Text>
              </Col>
            </Row>

            <div style={{ padding: "0px 16px" }}>
              <TablePagination showHeader={false} columns={columns} data={products} />
            </div>
          </div>
        ))}
      </ScrollContainer>
    </ModalStyled>
  );
}

export default ModalQuotaDup;
